<template>
  <button
    :type="submit ? 'submit' : 'button'"
    :disabled="disabled || loading"
    :aria-busy="loading"
    :class="[
      styles.button,
      styles[size],
      styles[type],
      {
        [styles['full']]: full,
        [styles.iconOnly]: $slots['left-icon'] && !$slots['default'] && !$slots['right-icon'],
      },
    ]"
  >
    <span :class="[styles.content, { [styles.hidden]: loading }]">
      <template v-if="empty">
        <slot />
      </template>
      <template v-else>
        <span v-if="$slots['left-icon']" :class="styles.icon">
          <slot name="left-icon" />
        </span>

        <typography v-if="$slots['default']" :variant="typographyVariant" :class="styles.text">
          <slot />
        </typography>

        <span v-if="$slots['right-icon']" :class="styles.icon">
          <slot name="right-icon" />
        </span>
      </template>
    </span>
    <span v-if="loading" :class="styles.loader">
      <Spinner :size="spinnerSize" :color="spinnerColor" />
    </span>
  </button>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

import type { TypographyVariant } from 'src/shared/ui';
import { Spinner } from 'src/shared/ui/spinner';

import styles from './styles.module.css';

export interface BaseButtonProps {
  submit?: boolean;
  type?: 'primary' | 'secondary' | 'outline' | 'flat' | 'raised' | 'warn' | 'link';
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | 'subm';
  disabled?: boolean;
  loading?: boolean;
  full?: boolean;
  // make button clear (without other content)
  empty?: boolean;
}

const props = withDefaults(defineProps<BaseButtonProps>(), {
  type: 'primary',
  size: 'm',
  disabled: false,
  loading: false,
  full: false,
  empty: false,
});
defineSlots<{
  default(props: object): never;
  'left-icon'(props: object): never;
  'right-icon'(props: object): never;
}>();

const sizeToTypographyMap: Record<Required<BaseButtonProps>['size'], TypographyVariant> = {
  xs: 'caption-2',
  s: 'caption-2',
  m: 'body-1',
  l: 'body-1',
  xl: 'body-3',
  xxl: 'body-3',
  subm: 'subheader-m',
};
const typographyVariant = computed<TypographyVariant>(() =>
  props.type === 'link' ? 'body-2' : sizeToTypographyMap[props.size],
);

const sizeToSpinnerSizeMap: Record<Required<BaseButtonProps>['size'], number> = {
  xs: 12,
  s: 12,
  m: 16,
  l: 16,
  xl: 20,
  xxl: 20,
  subm: 16,
};
const spinnerSize = computed<number>(() =>
  props.type === 'link' ? 32 : sizeToSpinnerSizeMap[props.size],
);

const spinnerColor = computed((): 'primary' | 'white' => {
  return props.type === 'primary' ? 'white' : 'primary';
});
</script>
